import { Plus, Trash2, ListPlus, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const sumOtherDetails = (rows) =>
  Math.round((rows || []).reduce((s, r) => s + num(r.amount), 0) * 10000) / 10000;

// 2026-10-27 · 行来源标识 —— FM 同步下来的 4 行使用 source="fm"，App 用户手工行为 "web" 或 ""。
const FM_SOURCE = "fm";
const isFmRow = (r) => (r?.source || "") === FM_SOURCE;

/**
 * 2026-06 · 其他明细 — freeform 名称 + 金额 rows whose sum feeds 采购报价's 其他.
 * 2026-10-27 · FM 的 其他/其他1/其他2/其他3 四个字段同步后作为 4 行写入 (source="fm")，
 * 在此对话框中以"仅只读"展示，用户编辑任意 FM 行 / 删除行都会被阻止。
 */
export function OtherCostDetailsDialog({ open, onOpenChange, rows, onChange }) {
  const { t } = useLanguage();
  const list = rows || [];
  const setRow = (i, patch) => {
    // 禁止修改 FM 来源行 (仅只读显示)
    if (isFmRow(list[i])) return;
    onChange(list.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeRow = (i) => {
    if (isFmRow(list[i])) return;
    onChange(list.filter((_, idx) => idx !== i));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pq-other-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ListPlus size={16} className="text-brand" /> {t("pq.otherDetails")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_120px_32px] gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            <span>{t("pq.otherDetailName")}</span>
            <span className="text-right">{t("pq.otherDetailAmount")}</span>
            <span />
          </div>
          {list.length === 0 && (
            <p className="text-xs text-muted-foreground py-3 text-center" data-testid="pq-other-details-empty">
              {t("pq.otherDetailsEmpty")}
            </p>
          )}
          {list.map((r, i) => {
            const fm = isFmRow(r);
            return (
              <div key={i} className="grid grid-cols-[1fr_120px_32px] gap-2 items-center"
                   data-testid={`pq-other-detail-row-${i}`}
                   data-source={r.source || "web"}>
                <div className="relative">
                  <Input value={r.name || ""} onChange={(e) => setRow(i, { name: e.target.value })}
                    readOnly={fm}
                    className={`h-8 text-sm ${fm ? "bg-amber-50 border-amber-200 text-amber-900 pr-6" : ""}`}
                    data-testid={`pq-other-detail-name-${i}`} />
                  {fm && (
                    <Lock
                      size={11}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-amber-500"
                      aria-label="FM read-only"
                    />
                  )}
                </div>
                <Input type="number" step="0.01" value={r.amount ?? ""} onChange={(e) => setRow(i, { amount: e.target.value })}
                  readOnly={fm}
                  className={`h-8 text-sm text-right font-mono ${fm ? "bg-amber-50 border-amber-200 text-amber-900" : ""}`}
                  data-testid={`pq-other-detail-amount-${i}`} />
                <button type="button" onClick={() => removeRow(i)}
                  disabled={fm}
                  title={fm ? (t("pq.otherDetailFmLocked") || "此行由 FileMaker 同步，不可删除") : undefined}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground disabled:cursor-not-allowed"
                  data-testid={`pq-other-detail-remove-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
          <Button type="button" variant="outline" size="sm" className="h-8 rounded-md"
            onClick={() => onChange([...list, { name: "", amount: "", source: "web" }])}
            data-testid="pq-other-detail-add">
            <Plus size={14} /> {t("pq.otherDetailAdd")}
          </Button>
          {list.some(isFmRow) && (
            <p className="text-[10px] text-amber-700/80 italic flex items-center gap-1 pt-1">
              <Lock size={10} />
              {t("pq.otherDetailFmNote") || "带锁的行来自 FileMaker (其他 / 其他1 / 其他2 / 其他3)，仅 FM 侧可改"}
            </p>
          )}

          <div className="flex items-center justify-between border-t border-border pt-2 mt-1">
            <span className="text-xs font-semibold">{t("pq.otherDetailsTotal")}</span>
            <span className="font-mono text-sm font-bold text-brand" data-testid="pq-other-details-total">
              {sumOtherDetails(list)}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="rounded-md bg-brand hover:bg-brand-hover"
            data-testid="pq-other-details-done">
            {t("common.done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
